import React, {useCallback} from 'react';


import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {bulkMfaStatusUpdateProgress, getMfaStatusUpdateResult} from 'src/openapi/admin/apis/BulkUpdateMfaStatusApi';
import {UpdateMfaStatusItem} from 'src/openapi/admin/models/UpdateMfaStatusItem';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {useGateValue} from '@pb/kit-lib-statsig';

import {FILE_HEADERS, FILE_NAME, LEGACY_FILE_HEADERS, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateUserMfaSettingsResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const emailPrimaryMfaEnabled = useGateValue(STATSIG_GATES.EMAIL_PRIMARY_MFA);

    const getPaginatedResults = useCallback(
        (bulkResult: UpdateMfaStatusItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UpdateMfaStatusItem, index) => (
                <Row key={res.email}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.email}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>{res.oldStatus ? 'Enabled' : 'Disabled'}</Cell>
                    <Cell>{res.newStatus ? 'Enabled' : 'Disabled'}</Cell>
                    {emailPrimaryMfaEnabled ? (
                        <>
                            <Cell>{res.oldEmailPrimaryFactorStatus ? 'Enabled' : 'Disabled'}</Cell>
                            <Cell>{res.newEmailPrimaryFactorStatus ? 'Enabled' : 'Disabled'}</Cell>
                        </>
                    ) : null}
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [emailPrimaryMfaEnabled],
    );

    return (
        <BulkActionResultModal<UpdateMfaStatusItem>
            id={id}
            onClose={onClose}
            width={1100}
            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={emailPrimaryMfaEnabled ? FILE_HEADERS : LEGACY_FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            userId: item.userId?.toString(),
                            email: item.email,
                            oldStatus: item.oldStatus ? 'Enabled' : 'Disabled',
                            newStatus: item.newStatus ? 'Enabled' : 'Disabled',
                            ...(emailPrimaryMfaEnabled ? {
                                oldEmailPrimaryFactorStatus: item.oldEmailPrimaryFactorStatus ? 'Enabled' : 'Disabled',
                                newEmailPrimaryFactorStatus: item.newEmailPrimaryFactorStatus ? 'Enabled' : 'Disabled',
                            } : {}),
                            actionStatus: item.actionStatus,
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={emailPrimaryMfaEnabled ? undefined : 200}>Email</Cell>
                        <Cell width={emailPrimaryMfaEnabled ? 125 : 200}>
                            {emailPrimaryMfaEnabled ? 'Old MFA Status' : 'Old Status'}
                        </Cell>
                        <Cell width={emailPrimaryMfaEnabled ? 130 : 200}>
                            {emailPrimaryMfaEnabled ? 'New MFA Status' : 'New Status'}
                        </Cell>
                        {emailPrimaryMfaEnabled ? (
                            <Cell width={160}>Old Email MFA Status</Cell>
                        ) : null}
                        {emailPrimaryMfaEnabled ? (
                            <Cell width={165}>New Email MFA Status</Cell>
                        ) : null}
                        <Cell width={emailPrimaryMfaEnabled ? 120 : undefined}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(bulkMfaStatusUpdateProgress({id}))}
            getResult={() => apiDispatch(getMfaStatusUpdateResult({id}))}
            sortComparator={(a, b) => {
                if (a.errors?.length && !b.errors?.length) {
                    return -1;
                }
                if (!a.errors?.length && b.errors?.length) {
                    return 1;
                }
                return a.email.localeCompare(b.email);
            }}
        />
    );
};
